import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Web as WebIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { Company } from '../../types';
import { useNavigate } from 'react-router-dom';
import airtableService from '../../services/airtable';

const CompaniesList: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await airtableService.getCompanies();

      if (response.success) {
        setCompanies(response.data);
      } else {
        setError(response.error || 'Failed to fetch companies');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Inactive': return 'default';
      case 'On Hold': return 'warning';
      default: return 'default';
    }
  };

  const getIndustryIcon = (industry: string) => {
    switch (industry.toLowerCase()) {
      case 'technology':
      case 'tech':
        return '💻';
      case 'finance':
      case 'financial':
        return '💰';
      case 'healthcare':
        return '🏥';
      case 'consulting':
        return '💼';
      case 'retail':
        return '🛍️';
      case 'manufacturing':
        return '🏭';
      default:
        return '🏢';
    }
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = `${company.name} ${company.industry} ${company.primaryContactName}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || company.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setFilterAnchor(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="h6">
          Unable to load companies. Please check your Airtable configuration.
        </Typography>
        <Button variant="outlined" onClick={fetchCompanies} sx={{ mt: 2 }}>
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Companies ({filteredCompanies.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/companies/new')}
        >
          Add Company
        </Button>
      </Box>

      {/* Search and Filter */}
      <Box display="flex" gap={2} mb={3}>
        <TextField
          placeholder="Search companies..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <IconButton onClick={handleFilterClick}>
          <FilterIcon />
        </IconButton>
        <Menu
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={handleFilterClose}
        >
          <MenuItem onClick={() => handleStatusFilter('')}>All Status</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('Active')}>Active</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('Inactive')}>Inactive</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('On Hold')}>On Hold</MenuItem>
        </Menu>
      </Box>

      {/* Companies Grid */}
      <Grid container spacing={3}>
        {filteredCompanies.map((company) => (
          <Grid item xs={12} md={6} lg={4} key={company.id}>
            <Card
              elevation={2}
              sx={{
                cursor: 'pointer',
                '&:hover': { elevation: 4 },
                transition: 'box-shadow 0.2s'
              }}
              onClick={() => navigate(`/companies/${company.id}`)}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box display="flex" alignItems="center" flexGrow={1}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                        mr: 2,
                        bgcolor: 'grey.100',
                        overflow: 'hidden'
                      }}
                    >
                      {company.logo && company.logo.length > 0 ? (
                        <img
                          src={company.logo[0].thumbnails?.small?.url || company.logo[0].url}
                          alt={`${company.name} logo`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        getIndustryIcon(company.industry)
                      )}
                    </Box>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {company.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {company.industry}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={company.status}
                    color={getStatusColor(company.status) as any}
                    size="small"
                  />
                </Box>

                <Box display="flex" alignItems="center" mb={2}>
                  <GroupIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {company.size} employees
                  </Typography>
                </Box>

                {company.website && (
                  <Box display="flex" alignItems="center" mb={2}>
                    <WebIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary" component="a" href={company.website} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      {company.website.replace(/^https?:\/\//, '')}
                    </Typography>
                  </Box>
                )}

                {company.address && (
                  <Box display="flex" alignItems="center" mb={2}>
                    <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {company.address}
                    </Typography>
                  </Box>
                )}

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 1 }}>
                  Primary Contact
                </Typography>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {company.primaryContactName}
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {company.primaryContactEmail}
                    </Typography>
                  </Box>
                  {company.primaryContactPhone && (
                    <Box display="flex" alignItems="center" mt={1}>
                      <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {company.primaryContactPhone}
                      </Typography>
                    </Box>
                  )}
                </Box>

              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {filteredCompanies.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            No companies found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search criteria or add a new company
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CompaniesList;